import type { AspectRatio } from '../../aspectRatioConfig';
import { aspectRatioConfigurations } from '../../aspectRatioConfig';
import { LOTTIE_ORIGINAL_DURATION } from '../../constants';
import type { LottieAssetDuration } from '../../core/asset/LottieAsset/LottieAsset';
import { assertInArray, assertMinimum, assertNumber, assertStringOrNumber } from '../../validators';
import { ErrorReporter } from '../error-reporter';
import { LAYER_NAME_SCENE_1, LAYER_NAME_SCENE_2 } from './plugins/TransitionsPlugin';
import type { Lottie, SolidColorLayer } from './types/lottie';
import { LayerType } from './types/lottie';

/**
 * Asserts whether the given object looks like a valid Lottie JSON structure.
 * This method checks for the presense of the mandatory fields 'v', 'ip', 'op', 'layers', 'fr', 'w', 'h' in the object.
 * Note: Lottie schema says 'v' is not mandatory, but Skottie requires it.
 */
export function assertIsValidLottie(json: Record<string, unknown>): asserts json is Lottie {
    const mandatory = ['v', 'ip', 'op', 'layers', 'fr', 'w', 'h'];
    const isValid = mandatory.every((field) => Object.prototype.hasOwnProperty.call(json, field));
    if (!isValid) {
        throw new Error('invalid: expected valid lottie json');
    }
}

export function assertIsValidLottieDuration(value: LottieAssetDuration) {
    assertStringOrNumber(value);
    if (typeof value === 'string') {
        assertInArray(value, [LOTTIE_ORIGINAL_DURATION]);
    } else {
        assertNumber(value);
        assertMinimum(value, 0);
    }
}

/**
 * Asserts whether the given object looks like a valid transition Lottie JSON structure.
 * Example transition Lottie JSON structure:
 * [ color transition layer for scene 2 (optional)                      ]
 * [ mask transition layer for scene 2 (optional)                       ]
 * [ scene 2 layer (required) / push transitions for scene 2 (optional) ]
 * [ color transition layer for scene 1 (optional)                      ]
 * [ mask transition layer for scene 1 (optional)                       ]
 * [ scene 1 layer (required) / push transitions for scene 1 (optional) ]
 * Push transitions for scene 1 and scene 2 are available on layers scene 1 and scene 2 respectively.
 */
export function assertIsValidTransitionLottie(json: Lottie, aspectRatio: AspectRatio) {
    assertIsValidLottie(json);
    let layerIndexScene1 = -1;
    let layerIndexScene2 = -1;
    for (let i = 0, n = json.layers.length; i < n; i++) {
        const layer = json.layers[i];
        if (layer.nm === LAYER_NAME_SCENE_1) {
            layerIndexScene1 = i;
        }
        if (layer.nm === LAYER_NAME_SCENE_2) {
            layerIndexScene2 = i;
        }
    }
    if (layerIndexScene1 === -1) {
        throw new Error(`invalid: expected layer with name "${LAYER_NAME_SCENE_1}"`);
    }
    if (layerIndexScene2 === -1) {
        throw new Error(`invalid: expected layer with name "${LAYER_NAME_SCENE_2}"`);
    }
    if (layerIndexScene1 < layerIndexScene2) {
        throw new Error(
            `invalid: expected layer "${LAYER_NAME_SCENE_1}" to be below "${LAYER_NAME_SCENE_2}"`,
        );
    }
    if (layerIndexScene1 !== json.layers.length - 1) {
        throw new Error(`invalid: expected layer "${LAYER_NAME_SCENE_1} to be the bottom layer`);
    }

    assertIsValidLottieSize(json, aspectRatio);
    assertDoesNotContainNullLayer(json);
    assertIsValidLottieLayerSize(json.layers[layerIndexScene1] as SolidColorLayer, aspectRatio);
    assertIsValidLottieLayerSize(json.layers[layerIndexScene2] as SolidColorLayer, aspectRatio);
}

function assertDoesNotContainNullLayer(json: Lottie) {
    for (let i = 0, n = json.layers.length; i < n; i++) {
        const layer = json.layers[i];
        if (layer.ty === LayerType.Null) {
            throw new Error('invalid: expected no null layers');
        }
    }
}

function assertIsValidLottieSize(json: Lottie, aspectRatio: AspectRatio) {
    const expectedSize = aspectRatioConfigurations[aspectRatio].referenceResolution;
    if (json.w !== expectedSize.width || json.h !== expectedSize.height) {
        ErrorReporter.setContext('Invalid Lottie size', {
            aspectRatio,
            expectedWidth: expectedSize.width,
            expectedHeight: expectedSize.height,
            actualWidth: json.w,
            actualHeight: json.h,
        });
        throw new Error('invalid: expected valid lottie size');
    }
}

function assertIsValidLottieLayerSize(json: SolidColorLayer, aspectRatio: AspectRatio) {
    if (!('sw' in json && 'sh' in json)) {
        return;
    }
    const expectedSize = aspectRatioConfigurations[aspectRatio].referenceResolution;
    if (json.sw !== expectedSize.width || json.sh !== expectedSize.height) {
        ErrorReporter.setContext('Invalid Lottie layer size', {
            aspectRatio,
            expectedWidth: expectedSize.width,
            expectedHeight: expectedSize.height,
            actualWidth: json.sw,
            actualHeight: json.sh,
        });
        throw new Error('invalid: expected valid lottie layer size');
    }
}
